import { createTheme } from '@mui/material/styles';

const getTypography = (direction) => {
  const fontFamily = direction === 'rtl'
    ? "'Vazirmatn', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
    : "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

  return {
    fontFamily,
    h5: {
      fontWeight: 700,
      letterSpacing: direction === 'rtl' ? '0' : '-0.02em',
    },
    h6: {
      fontWeight: 700,
      letterSpacing: direction === 'rtl' ? '0' : '-0.01em',
    },
    subtitle1: {
      fontWeight: 600,
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
      fontFamily,
    },
  };
};

export const getAppTheme = (mode, direction = 'ltr') => {
  const isDark = mode === 'dark';
  const fontFamily = direction === 'rtl'
    ? "'Vazirmatn', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
    : "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

  return createTheme({
    direction,
    palette: {
      mode,
      background: {
        default: isDark ? '#0B0F17' : '#F8FAFC',
        paper: isDark ? '#141C2C' : '#FFFFFF',
      },
      primary: {
        main: isDark ? '#3B82F6' : '#2563EB',
        light: '#60A5FA',
        dark: isDark ? '#2563EB' : '#1D4ED8',
        contrastText: '#FFFFFF',
      },
      secondary: {
        main: isDark ? '#10B981' : '#059669',
        light: '#34D399',
        dark: isDark ? '#059669' : '#047857',
      },
      info: {
        main: isDark ? '#38BDF8' : '#0284C7',
      },
      warning: {
        main: isDark ? '#F59E0B' : '#D97706',
      },
      text: {
        primary: isDark ? '#F8FAFC' : '#0F172A',
        secondary: isDark ? '#94A3B8' : '#64748B',
      },
      divider: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)',
    },
    typography: getTypography(direction),
    shape: {
      borderRadius: 16,
    },
    components: {
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? '#141C2C' : '#FFFFFF',
            backgroundImage: isDark ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.03) 0%, rgba(255, 255, 255, 0) 100%)' : 'none',
            border: isDark ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(226, 232, 240, 0.8)',
            boxShadow: isDark ? '0 4px 20px -2px rgba(0, 0, 0, 0.3)' : '0 4px 16px -2px rgba(15, 23, 42, 0.05)',
            transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              borderColor: isDark ? 'rgba(59, 130, 246, 0.4)' : 'rgba(37, 99, 235, 0.3)',
              boxShadow: isDark ? '0 12px 30px -4px rgba(0, 0, 0, 0.5), 0 0 15px 0 rgba(59, 130, 246, 0.15)' : '0 12px 24px -4px rgba(37, 99, 235, 0.12)',
              transform: 'translateY(-2px)',
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            padding: '8px 16px',
            fontFamily,
            gap: '6px',
            alignItems: 'center',
            justifyContent: 'center',
            lineHeight: 1.4,
          },
          startIcon: {
            margin: 0,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            '& > *:nth-of-type(1)': {
              fontSize: 'inherit',
              display: 'block',
            },
          },
          endIcon: {
            margin: 0,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            '& > *:nth-of-type(1)': {
              fontSize: 'inherit',
              display: 'block',
            },
          },
        },
      },
      MuiTooltip: {
        defaultProps: {
          disableTouchListener: true,
          enterTouchDelay: Infinity,
          leaveTouchDelay: 0,
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontFamily,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          },
          label: {
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            lineHeight: 1.2,
          },
        },
      },
    },
  });
};

export const darkTheme = getAppTheme('dark', 'ltr');
export const lightTheme = getAppTheme('light', 'ltr');